/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast, Slide } from 'react-toastify';

import { GlobalStyle } from 'styles/global-styles';

import { META_DESCRIPTION } from './constants';
import podReducer from './reducers';
import PodHomePage from 'containers/PodHomePage/Loadable';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import PodSelector from 'containers/PodSelector/Loadable';
import { AppStyles } from './style';

import sessionSaga from 'containers/Authentication/sagas';
import sessionReducer from 'containers/Authentication/reducers';
import { AuthRoute, ProtectedRoute } from 'containers/Authentication';
import LoginPage from 'containers/Authentication/LoginPage';
import AdminGate from 'containers/Admin/AdminGate';
import CreativePreview from 'containers/CreativePreview';
import NavbarProvider from 'containers/App/Navbar/NavbarProvider';
import UpdatePasswordPage from 'containers/Authentication/UpdatePasswordPage';
import RegisterPage from 'containers/Authentication/RegisterPage';
import VerifyEmailPage from 'containers/Authentication/VerifyEmailPage';
import AssetsPreview from 'containers/AssetsPreview';
import Header from './Header';
import NewCreativePreview from 'containers/NewCreativePreview';
import UserFeedbackLauncher from 'features/userFeedback/components/UserFeedbackLauncher';
import { stopSelectiveSessionReplay } from 'analytics/posthog';
import UnavailableAdminRoute from 'containers/Admin/UnavailableAdminRoute';

toast.configure({
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  newestOnTop: true,
  closeOnClick: false,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: false,
  pauseOnHover: true,
  transition: Slide,
  limit: 5,
});

export function App() {
  const { i18n } = useTranslation();
  useInjectReducer({
    key: podReducer.name,
    reducer: podReducer.reducer,
  });

  useInjectSaga({ key: 'session', saga: sessionSaga });
  useInjectReducer({ key: 'session', reducer: sessionReducer });

  return (
    <BrowserRouter>
      <ReplaySafetyCleanup />
      <Helmet
        defaultTitle="TV Testing UI"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content={META_DESCRIPTION} />
      </Helmet>
      <AppStyles>
        <NavbarProvider>
          <div className="app-container">
            <Header />
            <Switch>
              <Route
                exact
                path="/update-password"
                component={UpdatePasswordPage}
              />
              <Route exact path="/verify-email" component={VerifyEmailPage} />
              <Route exact path="/register" component={RegisterPage} />
              <AuthRoute exact path={'/login'} component={LoginPage} />
              <ProtectedRoute path="/" component={PodSelector} exact />
              <ProtectedRoute
                exact
                path="/admin-controls/user-guide-management"
                component={UnavailableAdminRoute}
              />
              <ProtectedRoute
                exact
                path="/admin-controls/user-feedback-management"
                component={UnavailableAdminRoute}
              />
              <ProtectedRoute path="/admin" component={AdminGate} />
              <ProtectedRoute
                path="/old-creative-preview"
                component={CreativePreview}
              />
              <ProtectedRoute
                path="/creative-preview"
                component={NewCreativePreview}
              />
              <ProtectedRoute
                path="/asset-preview/:fileName"
                component={AssetsPreview}
              />
              <ProtectedRoute
                exact
                path="/monitoring"
                component={UnavailableAdminRoute}
              />
              <ProtectedRoute path="/:pod" component={PodHomePage} />
            </Switch>
            <UserFeedbackLauncher />
          </div>
        </NavbarProvider>
        <GlobalStyle />
      </AppStyles>
    </BrowserRouter>
  );
}

function ReplaySafetyCleanup() {
  const location = useLocation();
  const previousRouteRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (
      previousRouteRef.current !== null &&
      previousRouteRef.current !== currentRoute
    ) {
      stopSelectiveSessionReplay();
    }
    previousRouteRef.current = currentRoute;
  }, [location.hash, location.pathname, location.search]);

  React.useEffect(() => {
    const stopReplay = () => stopSelectiveSessionReplay();

    window.addEventListener('pagehide', stopReplay);
    window.addEventListener('beforeunload', stopReplay);

    return () => {
      window.removeEventListener('pagehide', stopReplay);
      window.removeEventListener('beforeunload', stopReplay);
    };
  }, []);

  return null;
}
